import { CSSProperties, FC } from 'react';
import { useQuery } from '@tanstack/react-query';
import { asText } from '@prismicio/client';
import classNames from 'classnames';

import {
  Heading,
  Icon,
  Link,
  Product,
  ProductPlaceholder,
  Slider,
  useData,
} from '@/components';
import { useEcomStoreSelector } from '@/data';
import { useSelectedStore, useShowByLocationSlug } from '@/hooks';
import { IProductRowSlice } from '@/types';
import { getProductsFromOpenSearchResponse, getSlicePadding } from '@/utils';
import {
  buildSearchBodyFromParams,
  parseInventoryLocationIds,
  SearchService,
} from '@/opensearch';
import { useFulFillmentType } from '@/hooks/useFulFillmentType';

import styles from './productrow.module.scss';

const ProductsRow: FC<IProductRowSlice> = slice => {
  const { productCard } = useData();
  const {
    breakpoints,
    defaultSlidePerView,
    defaultSpaceBetween,
    spacingBetweenCard,
    isLargeCard,
  } = productCard.displayProps;
  const { store } = useSelectedStore();
  const { fulfillmentType } = useFulFillmentType();
  const { showContent } = useShowByLocationSlug(
    slice?.primary?.location_slug as string,
  );

  const initProducts = slice?.products ?? [];
  const selectedStore = store || slice?.selectedStore;
  const { provider_inventory_location_id } = useEcomStoreSelector([
    'provider_inventory_location_id',
  ]);
  const inventoryLocationIds = parseInventoryLocationIds(
    provider_inventory_location_id,
  );
  const paddingSection = slice?.primary?.padding_section;
  const headerColor = slice?.primary?.header_color;
  const marginBottom = slice?.primary?.bottom_spacing;
  const backgroundColor = slice?.primary?.background_color;
  const backgroundImage = slice?.primary?.background_image?.url;
  const backgroundStyleFixed = slice?.primary?.background_style_fixed_cover;
  const minHeight = slice?.primary?.min_heigth;
  const seeAllHref = slice?.primary?.see_all_link;
  const seeMoreText = slice?.primary?.see_all_text ?? 'SEE ALL';
  const seeAllTextColor = slice?.primary?.see_all_text_color;
  const activeControlColor = slice?.primary?.active_control_color;
  const disableControlColor = slice?.primary?.disable_control_color;

  const isFilterByStore = slice?.primary?.filter_by_store;

  const ids = slice?.items
    ?.map(item => item.product_id?.trim())
    .filter((id): id is string => !!id);

  const queryKey = [
    'searchProducts_product_row',
    store?.short_name,
    provider_inventory_location_id,
    fulfillmentType,
    ...ids,
  ];

  const { search } = new SearchService({
    entityId: selectedStore?.mso_store_entity_id,
    orgId: selectedStore?.mso_org_id,
    isStrictMedicalEnabled: selectedStore?.strict_medical_enabled,
    inventoryLocationIds,
    fulfillmentType,
  });

  const { data, isLoading } = useQuery({
    enabled: isFilterByStore && ids.length > 0,
    initialData: {
      products: initProducts,
    },
    queryKey,
    queryFn: async () => {
      const idParam = { id: ids };

      const { searchBody } = buildSearchBodyFromParams({
        searchParams: {
          ...idParam,
        },
        omitAggs: true,
      });

      const result = await search({ searchBody });

      const products = getProductsFromOpenSearchResponse({ result });

      return { products };
    },
  });

  const productsCollection = data?.products;

  const background = seeAllTextColor ? 'transparent' : 'initial';
  const productsHeaderText = asText(slice?.primary?.products_header);
  const ariaLabel = productsHeaderText
    ? `${productsHeaderText} carousel`
    : 'Products rowcarousel';

  if (!slice?.primary?.active || !productsCollection?.length || !showContent) {
    return null;
  }

  const carouselStyle = {
    ...(activeControlColor
      ? { '--products-control-color': activeControlColor }
      : {}),
    ...(disableControlColor
      ? { '--products-control-disabled-color': disableControlColor }
      : {}),
    ...(seeAllTextColor ? { '--products-control-border': 'transparent' } : {}),
  } as CSSProperties;

  return (
    <section
      aria-label={ariaLabel}
      className={classNames(
        styles.product_row__section,
        getSlicePadding(paddingSection),
        {
          ['spacing_bottom']: marginBottom,
          ['background__fixed_cover']: backgroundStyleFixed,
          ['background__contained']: !backgroundStyleFixed,
        },
      )}
      style={{
        background: backgroundColor!,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        minHeight: `${minHeight || 0}vh`,
        ...carouselStyle,
      }}
    >
      <Slider
        activatedHover
        ariaLabel={ariaLabel}
        breakpoints={breakpoints}
        centeredSlides={false}
        className="products__row"
        freeMode
        isNotPaddingMobile={spacingBetweenCard === 'none'}
        seeAllElement={
          <Link
            aria-label={`Go to ${seeAllHref ?? '/deals'}`}
            className={styles.products__see_all}
            href={seeAllHref ?? '/deals'}
            style={{ color: seeAllTextColor! }}
          >
            {seeMoreText} <span>({initProducts.length})</span>
          </Link>
        }
        showSliderCardLink={true}
        sliderHeading={
          <Heading level={2} style={{ color: headerColor! }}>
            {productsHeaderText}
          </Heading>
        }
        slidesPerView={defaultSlidePerView}
        spaceBetween={defaultSpaceBetween}
      >
        {isLoading
          ? new Array(20)
              .fill(null)
              .map((_, index) => (
                <ProductPlaceholder key={index} largeCard={isLargeCard} />
              ))
          : productsCollection?.map(product => (
              <Product
                className={styles.slide__main}
                key={product.id}
                product={product}
              />
            ))}
        <Link
          aria-label={`Go to ${seeAllHref ?? '/deals'}`}
          className={classNames(
            styles.slide__main,
            styles.product__shop_all_slider,
          )}
          href={seeAllHref ?? '/deals'}
          style={{
            color: seeAllTextColor!,
            background: background,
          }}
        >
          <Icon name="right" />
          <p
            style={{
              color: seeAllTextColor!,
            }}
          >
            {seeMoreText}
          </p>
        </Link>
      </Slider>
    </section>
  );
};
export default ProductsRow;
